import { useCallback, useEffect, useMemo, useState } from "react";
import IncomingOrder from "./order_card/incoming_order";
import { OrderResponse } from "../../../../api/order.api";
import CancelOrder from "../../modal_page/cancelOrder";

type Props = {
  dataOrdered?: OrderResponse[];
  dataInProgress?: OrderResponse[];
  dataReady?: OrderResponse[];
  fetchOrder?: () => void;
  oldSelected: string;
  setOld: (id: string) => void;
};

const C = {
  espresso: "#5B3A29",
  caramel:  "#D97706",
  rose:     "#E11D48",
};

const FILTERS = [
  { value: "all",    label: "All" },
  { value: "coffee", label: "Coffee" },
  { value: "other",  label: "Non-Caffeine" },
] as const;

type Channel = "drinks" | "desserts";

function isDessert(o: OrderResponse): boolean {
  return o.menu?.category?.categoryFor === "dessert";
}

function channelFilter(o: OrderResponse, c: Channel): boolean {
  return c === "desserts" ? isDessert(o) : !isDessert(o);
}

function categoryFilter(item: OrderResponse, key: string): boolean {
  const name = item.menu.category.name.toLowerCase();
  if (key === "all") return true;
  if (key === "coffee") return name === "coffee";
  // "other" = Non-Caffeine bucket. Tea was merged in here because it's
  // also caffeine-free in practice (we serve herbal / Thai-tea variants),
  // and the separate Tea tab was just an extra click for barista.
  return name !== "coffee";
}

function OrderingContent({
  fetchOrder,
  dataInProgress = [],
  dataOrdered = [],
  dataReady = [],
  setOld,
  oldSelected,
}: Props) {
  const [channel, setChannel] = useState<Channel>("drinks");
  const [filter, setFilter] = useState<string>(oldSelected || "all");
  const [isEdit, setIsEdit] = useState(false);
  const [bulkEditArray, setBulkEditArray] = useState<number[]>([]);
  const [rejectIDs, setRejectIDs] = useState<number[]>([]);

  useEffect(() => {
    setOld(filter);
  }, [filter, setOld]);

  const drinksCount = useMemo(
    () => [...dataInProgress, ...dataOrdered, ...dataReady].filter((o) => !isDessert(o)).length,
    [dataInProgress, dataOrdered, dataReady],
  );
  const dessertsCount = useMemo(
    () => [...dataInProgress, ...dataOrdered, ...dataReady].filter(isDessert).length,
    [dataInProgress, dataOrdered, dataReady],
  );

  const applyFilters = useCallback((rows: OrderResponse[]) => {
    return rows
      .filter((o) => channelFilter(o, channel))
      .filter((o) => channel === "desserts" ? true : categoryFilter(o, filter))
      .sort((a, b) => a.id - b.id);
  }, [channel, filter]);

  const inProgress = useMemo(() => applyFilters(dataInProgress), [dataInProgress, applyFilters]);
  const incoming   = useMemo(() => applyFilters(dataOrdered),   [dataOrdered,   applyFilters]);
  const ready      = useMemo(() => applyFilters(dataReady),     [dataReady,     applyFilters]);

  const empty = inProgress.length === 0 && incoming.length === 0 && ready.length === 0;

  const toggleBulk = (id: number) => {
    setBulkEditArray((curr) => (curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]));
  };

  const handleBulkCancel = () => {
    if (bulkEditArray.length > 0) setRejectIDs(bulkEditArray);
  };

  const closeRejectModal = () => {
    setRejectIDs([]);
    setBulkEditArray([]);
    setIsEdit(false);
    fetchOrder?.();
  };

  return (
    <>
      {/* @ts-ignore */}
      <CancelOrder rejectArray={rejectIDs} onClose={closeRejectModal} />

      {/* Channel toggle */}
      <div
        className="flex gap-2 p-1 rounded-2xl mb-3"
        style={{ background: "white", boxShadow: "0 0 0 1px #E7DFD2" }}
      >
        {([
          { id: "drinks",   label: "Drinks",   emoji: "☕", count: drinksCount },
          { id: "desserts", label: "Desserts", emoji: "🍰", count: dessertsCount },
        ] as const).map((c) => {
          const active = channel === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setChannel(c.id)}
              className="flex-1 inline-flex items-center justify-center gap-2 h-[40px] rounded-xl text-sm font-bold transition-all"
              style={
                active
                  ? { background: C.espresso, color: "#FFFBEB" }
                  : { background: "transparent", color: C.espresso }
              }
            >
              <span aria-hidden>{c.emoji}</span>
              <span>{c.label}</span>
              <span
                className="inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[10px] font-black tabular-nums"
                style={
                  active
                    ? { background: "rgba(255,251,235,0.25)", color: "#FFFBEB" }
                    : { background: "#F3EBDA", color: C.espresso }
                }
              >
                {c.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {channel === "drinks" && FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className="inline-flex items-center h-[32px] px-3.5 rounded-full text-xs font-bold whitespace-nowrap transition-all"
                style={
                  active
                    ? { background: C.espresso, color: "#FFFBEB" }
                    : { background: "white", color: C.espresso, boxShadow: "0 0 0 1px #E7DFD2" }
                }
              >
                {f.label}
              </button>
            );
          })}
        </div>
        {!isEdit ? (
          <button
            type="button"
            onClick={() => setIsEdit(true)}
            className="inline-flex items-center h-[32px] px-3.5 rounded-full text-xs font-bold transition-all"
            style={{ background: "transparent", color: C.espresso }}
          >
            Select multiple
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setIsEdit(false); setBulkEditArray([]); }}
              className="inline-flex items-center h-[32px] px-3.5 rounded-full text-xs font-bold transition-all"
              style={{ background: "white", color: C.espresso, boxShadow: `0 0 0 1px ${C.espresso}` }}
            >
              Done
            </button>
            <button
              type="button"
              onClick={handleBulkCancel}
              disabled={bulkEditArray.length === 0}
              className="inline-flex items-center h-[32px] px-3.5 rounded-full text-xs font-bold text-white transition-all disabled:opacity-40"
              style={{ background: C.rose }}
            >
              Cancel ({bulkEditArray.length})
            </button>
          </div>
        )}
      </div>

      {empty ? (
        <div
          className="bg-white rounded-2xl py-[60px] flex flex-col items-center text-center"
          style={{ boxShadow: "0 1px 0 #ECE5D5" }}
        >
          <div
            className="w-[64px] h-[64px] rounded-full flex items-center justify-center mb-4 text-3xl"
            style={{ background: "#F3EBDA" }}
          >
            {channel === "desserts" ? "🍰" : "☕"}
          </div>
          <p className="font-display font-black text-lg" style={{ color: "#1B0F08" }}>
            {channel === "desserts" ? "No desserts in the queue" : "No drinks in the queue"}
          </p>
          <p className="text-sm mt-1" style={{ color: "#A38B6A" }}>
            New orders will appear here automatically
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {ready.length > 0 && (
            <IncomingOrder
              reset={() => fetchOrder?.()}
              status="ready"
              editorMode={isEdit}
              data={ready}
              select={toggleBulk}
              selected={bulkEditArray}
            />
          )}
          {inProgress.length > 0 && (
            <IncomingOrder
              reset={() => fetchOrder?.()}
              status="inprogress"
              editorMode={isEdit}
              data={inProgress}
              select={toggleBulk}
              selected={bulkEditArray}
            />
          )}
          {incoming.length > 0 && (
            <IncomingOrder
              reset={() => fetchOrder?.()}
              status="incoming"
              editorMode={isEdit}
              data={incoming}
              select={toggleBulk}
              selected={bulkEditArray}
            />
          )}
        </div>
      )}
    </>
  );
}

export default OrderingContent;
